import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Grid,
  IconButton
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { format, parseISO, startOfMonth } from 'date-fns';
import api from './axiosConfig';

// UK tax year runs 6 April to 5 April, matching generate_tax_report() in
// secure_report_routes.py (start_date = {year}-04-06, end_date =
// {year+1}-04-05). Previously this component used a plain Jan-Dec
// calendar year, so "Total Accrued for 2026" here and "Tax Year
// 2026-2027" on TaxReport.js covered two different 12-month windows
// that didn't reconcile. This aligns the two.
const getCurrentTaxYearBounds = (referenceDate = new Date()) => {
  const year = referenceDate.getFullYear();
  // 6 April of the current calendar year, for comparison
  const aprilSixThisYear = new Date(year, 3, 6); // month is 0-indexed: 3 = April

  let startYear;
  if (referenceDate >= aprilSixThisYear) {
    startYear = year;
  } else {
    startYear = year - 1;
  }
  const endYear = startYear + 1;

  return {
    startYear,
    endYear,
    displayLabel: `${startYear}-${endYear}`,
    startDate: new Date(startYear, 3, 6),   // 6 April startYear
    endDate: new Date(endYear, 3, 5)        // 5 April endYear
  };
};

// Safe currency formatter — guards against null/undefined accrual
// amounts the way formatCurrency does in TaxReport.js and
// ProfitLossReport.js. Previously this component called
// amount.toFixed(2) directly, which would throw and crash the whole
// table if any single row had a null/undefined amount.
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) {
    return '£0.00';
  }
  try {
    return `£${parseFloat(amount).toFixed(2)}`;
  } catch (e) {
    return '£0.00';
  }
};

const TaxAccrual = ({ accountId }) => {
  const [accruals, setAccruals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openSetupDialog, setOpenSetupDialog] = useState(false);
  const [accrualDate, setAccrualDate] = useState(startOfMonth(new Date()));
  const [accrualAmount, setAccrualAmount] = useState('');
  const [accrualDescription, setAccrualDescription] = useState('');
  const [totalAnnualTax, setTotalAnnualTax] = useState('');
  const [setupYear, setSetupYear] = useState(new Date().getFullYear());
  const [setupDescription, setSetupDescription] = useState('');

  // Current UK tax year bounds, used both for the fetch query and the
  // "Total Accrued for ..." card label below.
  const taxYearBounds = getCurrentTaxYearBounds();

  const fetchTaxAccruals = async () => {
    setLoading(true);
    try {
      const formattedStartDate = format(taxYearBounds.startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(taxYearBounds.endDate, 'yyyy-MM-dd');
      
      const response = await api.get(`/accounting/tax-accruals`, {
        params: {
          start_date: formattedStartDate,
          end_date: formattedEndDate
        }
      });
      
      if (response.data && response.data.accruals) {
        // Sort accruals by month
        const sortedAccruals = [...response.data.accruals].sort((a, b) => {
          return new Date(a.month) - new Date(b.month);
        });
        setAccruals(sortedAccruals);
      }
    } catch (err) {
      console.error('Error fetching tax accruals:', err);
      setError('Failed to fetch tax accruals. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTaxAccruals();
  }, [accountId]);
  
  const handleAddAccrual = async () => {
    try {
      await api.post('/accounting/tax-accruals', {
        month_date: format(accrualDate, 'yyyy-MM-dd'),
        amount: parseFloat(accrualAmount),
        description: accrualDescription || undefined
      });
      
      setOpenAddDialog(false);
      resetFormFields();
      fetchTaxAccruals(); // Refresh data
    } catch (err) {
      console.error('Error adding tax accrual:', err);
      setError('Failed to add tax accrual. Please try again.');
    }
  };
  
  // Recalculates the current month and refreshes any month that already holds a
  // live figure, so an old snapshot can't sit there showing out-of-date numbers.
  // It does not create rows for months that have none.
  const handleRecalculate = async () => {
    setError(null);
    setInfoMessage(null);
    try {
      const currentMonth = format(startOfMonth(new Date()), 'yyyy-MM-dd');
      const months = new Set(
        accruals
          .filter((a) => a.source === 'live')
          .map((a) => format(startOfMonth(parseISO(a.month)), 'yyyy-MM-dd'))
      );
      months.add(currentMonth);
      const ordered = Array.from(months).sort();

      for (const month of ordered) {
        const response = await api.post('/accounting/tax-accruals/recalculate', {
          month_date: month
        });
        if (response.data.not_applicable) {
          setInfoMessage(response.data.message);
          return;
        }
        // Only the current month's failure matters to the person using the page.
        if (response.data.fallback_failed && month === currentMonth) {
          setError(response.data.message);
          return;
        }
      }
      fetchTaxAccruals();
    } catch (err) {
      console.error('Error recalculating tax accrual:', err);
      setError(err.response?.data?.message || 'Failed to recalculate tax accrual.');
    }
  };
  
  const handleAnnualSetup = async () => {
    try {
      await api.post('/accounting/tax-accruals/annual-setup', {
        year: setupYear,
        total_annual_tax: parseFloat(totalAnnualTax),
        description: setupDescription || undefined
      });
      
      setOpenSetupDialog(false);
      resetFormFields();
      fetchTaxAccruals(); // Refresh data
    } catch (err) {
      console.error('Error setting up annual accruals:', err);
      setError('Failed to set up annual accruals. Please try again.');
    }
  };
  
  const handleDeleteAccrual = async (accrualId) => {
    try {
      await api.delete(`/accounting/tax-accruals/${accrualId}`);
      fetchTaxAccruals(); // Refresh data
    } catch (err) {
      console.error('Error deleting tax accrual:', err);
      setError('Failed to delete tax accrual. Please try again.');
    }
  };
  
  const resetFormFields = () => {
    setAccrualDate(startOfMonth(new Date()));
    setAccrualAmount('');
    setAccrualDescription('');
    setTotalAnnualTax('');
    setSetupYear(new Date().getFullYear());
    setSetupDescription('');
  };
  
  const calculateTotalAccrued = () => {
    return accruals.reduce((sum, accrual) => sum + (parseFloat(accrual.amount) || 0), 0);
  };

  // Fix (21 Aug 2026): was previously hardcoded / 12 inline in the JSX
  // below, which understated the average for any partial year (e.g.
  // 4 months of real entries showed / 12 = £163.06 instead of the real
  // £489.20 average over the 4 months that actually have data).
  // accruals.length is safe here because create_monthly_accrual()
  // upserts on (account_id, month) — at most one row per month, so
  // accruals.length always equals "months with an accrual entry."
  const calculateMonthlyAverage = () => {
    if (accruals.length === 0) return 0;
    return calculateTotalAccrued() / accruals.length;
  };
  
  // Each live row is "total tax and NI so far this tax year, divided by the months
  // elapsed". So the rows are rates, not amounts to be added together. Use the latest
  // live row for the headline and rebuild the year-to-date liability from it.
  const latestAccrual = accruals.length > 0 ? accruals[accruals.length - 1] : null;
  const latestIsLive = latestAccrual && latestAccrual.source === 'live';
  let monthsSoFar = 0;
  if (latestIsLive) {
    const m = parseISO(latestAccrual.month);
    monthsSoFar = Math.max(
      1,
      (m.getFullYear() - taxYearBounds.startYear) * 12 + (m.getMonth() - 3) + 1
    );
  }
  const latestRate = latestIsLive ? parseFloat(latestAccrual.amount) || 0 : 0;
  const liabilitySoFar = latestRate * monthsSoFar;
  const profitSoFar = latestIsLive && latestAccrual.ytd_taxable_profit != null
    ? parseFloat(latestAccrual.ytd_taxable_profit)
    : null;

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Paper>
    );
  }
  
  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Tax Accruals</Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<AddCircleOutlineIcon />} 
            onClick={() => setOpenAddDialog(true)}
            sx={{ mr: 1 }}
          >
            Add Accrual
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleRecalculate}
            sx={{ mr: 1 }}
          >
            Recalculate from P&L
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setOpenSetupDialog(true)}
          >
            Annual Setup
          </Button>
        </Box>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {infoMessage && <Alert severity="info" sx={{ mb: 2 }}>{infoMessage}</Alert>}
      
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
            {latestIsLive ? (
              <>
                <Typography variant="subtitle2">Set aside each month, tax year {taxYearBounds.displayLabel}</Typography>
                <Typography variant="h4">{formatCurrency(latestRate)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Tax and National Insurance so far this tax year: {formatCurrency(liabilitySoFar)}
                  {profitSoFar !== null ? `, on profit of ${formatCurrency(profitSoFar)}` : ''}.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Based on the latest month shown below. Each month's figure replaces the last; they are not added together. Estimate only, not tax advice.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="subtitle2">Accrued (recorded months only), tax year {taxYearBounds.displayLabel}</Typography>
                <Typography variant="h4">{formatCurrency(calculateTotalAccrued())}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Average per recorded month: {formatCurrency(calculateMonthlyAverage())}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accruals.length > 0 ? (
              accruals.map((accrual) => (
                <TableRow key={accrual.id}>
                  <TableCell>{format(parseISO(accrual.month), 'MMMM yyyy')}</TableCell>
                  <TableCell>{accrual.description || '-'}</TableCell>
                  <TableCell align="right">{formatCurrency(accrual.amount)}</TableCell>
                  <TableCell align="center">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteAccrual(accrual.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">No tax accruals found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Add Accrual Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Add Tax Accrual</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, pb: 2, minWidth: '300px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Accrual Month"
                views={['year', 'month']}
                value={accrualDate}
                onChange={(newValue) => setAccrualDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
              />
            </LocalizationProvider>
            
            <TextField
              label="Amount"
              type="number"
              value={accrualAmount}
              onChange={(e) => setAccrualAmount(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                startAdornment: <span>£</span>,
              }}
            />
            
            <TextField
              label="Description"
              value={accrualDescription}
              onChange={(e) => setAccrualDescription(e.target.value)}
              fullWidth
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddAccrual} 
            variant="contained" 
            color="primary"
            disabled={!accrualAmount}
          >
            Add Accrual
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Annual Setup Dialog */}
      <Dialog open={openSetupDialog} onClose={() => setOpenSetupDialog(false)}>
        <DialogTitle>Annual Tax Accrual Setup</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, pb: 2, minWidth: '300px' }}>
            <Typography variant="body2" gutterBottom>
              This will create monthly accruals for the entire year, evenly distributing the total tax amount.
            </Typography>
            
            <TextField
              label="Year"
              type="number"
              value={setupYear}
              onChange={(e) => setSetupYear(parseInt(e.target.value))}
              fullWidth
              margin="normal"
            />
            
            <TextField
              label="Total Annual Tax"
              type="number"
              value={totalAnnualTax}
              onChange={(e) => setTotalAnnualTax(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                startAdornment: <span>£</span>,
              }}
            />
            
            <TextField
              label="Description"
              value={setupDescription}
              onChange={(e) => setSetupDescription(e.target.value)}
              fullWidth
              margin="normal"
            />
            
            {totalAnnualTax && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This will create 12 monthly accruals of {formatCurrency(parseFloat(totalAnnualTax) / 12)} each.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSetupDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAnnualSetup} 
            variant="contained" 
            color="primary"
            disabled={!totalAnnualTax}
          >
            Create Accruals
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TaxAccrual;